<template>
    <div class="h--100 relative min-h-inherit">
        <div :class="{ 'disabled': deleteLoading, 'min-h-inherit': !clients.length }">
            <template v-if="fetchClients.loading">
                <div class="revenue__stats__list">
                    <StatsSkeleton v-for="item in 3" :key="item" />
                </div>

                <SkeletonPageList class="mt--40" />
            </template>

            <template v-else-if="fetchClients.err">
                <EmptyPage state="error" @action="handleFetchClients" />
            </template>

            <template v-else>
                <div class="mb--25">
                    <template v-if="statsLoading">
                        <div class="revenue__stats__list">
                            <StatsSkeleton v-for="item in 3" :key="item" />
                        </div>
                    </template>

                    <template v-else-if="fetchStatsErr">
                        <EmptyPage state="error" type="revenue" @action="handleFetchStats" />
                    </template>   

                    <template v-else>
                        <div class="revenue__stats__list">
                            <div class="revenue__stats__list__item">
                                <ClientListStat
                                    title="Total clients created"
                                    :value="clientListStats.totalClients"
                                    tooltip-text="Showing all clients including archived clients."
                                />
                            </div>
        
                            <div class="revenue__stats__list__item">
                                <ClientListStat
                                    title="Total clients onboarded"
                                    :value="clientListStats.totalOnboarded"
                                    tooltip-text="Showing all active and inactive clients."
                                />
                            </div>
        
                            <div class="revenue__stats__list__item">
                                <ClientListStat
                                    title="Total clients active"
                                    :value="clientListStats.totalActive"
                                    tooltip-text="Showing all active clients"
                                />
                            </div>
                        </div>
                    </template>
                </div>

                <div>
                    <div class="flex align-items-center justify-content-between width-100">
                        <div v-if="clients.length">
                            <p class="table__striped__footer__results">
                                COUNT: <span>{{ clients.length }}</span>
                            </p>
                        </div>
                        
                        <div v-if="pageData.total || isSearched" class="flex align-items-center gap-2" :class="{ 'disabled': checkUpgradeLoading && checkUpgradeFields.clients }">
                            <BaseButton 
                                v-show="selectedClients.length > 0"
                                variant="warning" 
                                is-content
                                @submit="toggleDelete"
                            >
                                <template #content>
                                    Delete {{ selectedClients.length }} {{ selectedClients.length === 1 ? 'item' : 'items' }}
                                </template>
                            </BaseButton>
                            
                            <BaseButton 
                                label="+ Add client" 
                                @submit="showAddModal = true"
                            />
                            <ImportButton
                                v-if="accountPlan !== 'free'" label="Import" width="16px" icon-name="import"
                                @select="setImportType"
                            />
                            <MainFilter page-type="clients" />
                            <SortFilter :value="displayType" size="sm" label-value="Name" @setType="setDisplayType" />
                            <ImportButton
                                size="sm" label="Export" icon-name="export" :disabled="downloadLoading"
                                @select="downloadClients"
                            />
                        </div>
                    </div>
    
                    <div class="w-100" :class="{ 'justify-content-center flex align-items-center': !clients.length }">
                        <template v-if="!clients.length">
                            <EmptyPage v-if="isSearched" state="no_result" />
                            
                            <EmptyPage
                                v-else 
                                state="empty_custom" 
                                type="client" 
                                btn-state="custom" 
                                icon-name="user"
                                position="left"
                                title="No clients created" 
                                subtitle="Start an active database of clients and potential leads. Client statuses can be manually or automatically updated depending on their relative feedback stage."
                            >
                                <template #button>
                                    <DropDownButton 
                                        id="clients" 
                                        variant="default"
                                        variant-style="primary"
                                        label="+ Choose action" 
                                        class="ml--5"
                                        size="sm"
                                    >
                                        <template #dropdown-content>
                                            <ListItem pointer flex @click="showAddModal = true">
                                                <IconSvg name="user" />
                                                <span class="text--sm text-gray-800 ml--5">+ New client</span>
                                            </ListItem>

                                            <ListItem pointer flex @click="setImportType('csv')">
                                                <IconSvg name="spreadsheet" />
                                                <span class="text--sm text-gray-800 ml--5">Import CSV file</span>
                                            </ListItem>

                                            <ListItem pointer flex @click="setImportType('json')">
                                                <IconSvg name="file-json" />
                                                <span class="text--sm text-gray-800 ml--5">Import JSON file</span>
                                            </ListItem>
                                        </template>
                                    </DropDownButton>
                                </template>
                            </EmptyPage>
                        </template>
                        
                        <template v-else>
                            <div class="flex mt--40">
                                <CustomTable
                                    v-model="selectedClients"
                                    :columns="clientHeaders" :data="clients"
                                    router-name="client-details-view"
                                    row-type="link" table-type="striped"
                                    @onRowClick="openClientDetailsPage"
                                    @onRowSelected="selectClients"
                                >
                                    <template #rows="data">
                                        <td class="text--capitalize pl--0 first">
                                            {{ data.name || "-" }}
                                        </td>
                                        <td class="pl--0 first">
                                            {{ data.email || "-" }}
                                        </td>
                                        <td class="text--capitalize pl--0">
                                            {{ data.industry || "-" }}
                                        </td>
                                        <td class="pl--0">
                                            <Status type="client" :value="data.status" />
                                        </td>
                                        <td class="pl--0">
                                            {{ $format_date(data.createdAt) || "-" }}
                                        </td>
                                    </template>
                                </CustomTable>
                            </div>
    
                            <div class="table__striped__footer mt--5">
                                <Pagination 
                                    v-if="pageData.totalPages > 1" 
                                    :pagination-data="pageData" 
                                />
                            </div>
                        </template>
                    </div>
                </div>
            </template>            
        </div>

        <CreateClientModal 
            :show-modal="showAddModal" 
            :loading="createLoading"
            @cancel="closeCreateModal" 
            @complete="createNewClient"
        />

        <ConfirmDeletion
            type="client" custom-msg
            :show-modal="showDeleteModal" :items="selectedClients"
            :loading="deleteLoading" 
            @cancel="showDeleteModal = false" 
            @delete="deleteMultipleClients"
        >
            <template #custom-msg>
                <p class="text--sm">
                    The selected item(s) will be removed from all related projects, meetings, and invoices. <strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <FileUploadModal 
            :show="showUploadModal" 
            :mime-types="importType === 'csv' ? 'text/csv' : 'application/json'"
            :file-content="uploadContent" 
            :import-type="importType"
            :is-error="uploadErr" 
            :headers="selectedUploadHeaders"
            :progress-value="uploadProgress" 
            @cancel="closeFileModal" 
            @update:files="handleFileUpload" 
        >
            <template #CustomHeadersPick>
                <div class="flex align-items-center flex-wrap mt--20">
                    <div v-for="item in clientUploadHeaders" :key="item">
                        <BaseCheckbox  
                            :values.sync="selectedUploadHeaderIds" 
                            :value="item.id" 
                            :disabled="disabledHeaderIds.includes(item.id)"
                            index-label="id"
                            size="xs"
                            @checked="updateHeaderSelection(item, $event)"
                        >
                            {{ item.title }}
                        </BaseCheckbox>
                    </div>
                </div>
            </template>
        </FileUploadModal>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal || showUpgradeModalAlt" 
            type="clients" 
            @cancel="closeUpgradeModal"
        />
    </div>
</template>

<script>
import {
    MainFilter,
    SortFilter,
    Pagination,
    EmptyPage,
    CreateClientModal,
    ConfirmDeletion,
    BaseButton,
    CustomTable,
    ImportButton,
    Status,
    FileUploadModal, 
    UpgradeAccountModal,
    BaseCheckbox,
    ListItem,
    IconSvg,
} from "@/components/ui/index";
import { assembleQueryList } from "@/utils/helpers";
import { clientHeaders, clientUploadHeaders } from "@/utils/dummy";
import Papa from "papaparse";
import { MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import ClientListStat from "@/components/clients/ClientListStat.vue";
import StatsSkeleton from "@/components/stats/StatsSkeleton.vue";
import SkeletonPageList from "@/components/ui/loaders/SkeletonPageList.vue";
import DropDownButton from "@/components/ui/buttons/DropDownButton.vue";

export default {
    name: "ClientsPage",

    components: {
        ImportButton,
        CustomTable,
        CreateClientModal,
        ConfirmDeletion,
        EmptyPage,
        Pagination,
        MainFilter,
        SortFilter,
        BaseButton,
        Status,
        UpgradeAccountModal,
        FileUploadModal,
        BaseCheckbox,
        ClientListStat,
        StatsSkeleton,
        SkeletonPageList,
        DropDownButton,
        ListItem,
        IconSvg,
    },

    data() {
        return {
            clientUploadHeaders,
            clientHeaders,
            showAddModal: false,
            displayType: "",
            selectedClients: [],
            deleteLoading: false,
            showDeleteModal: false,
            fetchClients: {
                loading: false,
                err: false,
                msg: "",
            },
            importType: "",
            downloadType: "",
            showUploadModal: false,
            uploadProgress: 0,
            uploadContent: [],
            uploadErr: false,
            downloadLoading: false,
            showUpgradeModalAlt: false,
            selectedUploadHeaderIds: [],
            disabledHeaderIds: ["name", "email"],
            statsLoading: false,
            fetchStatsErr: false,
            createLoading: false,
        };
    },

    computed: {
        isSearched() {
            return Object.keys(this.$route.query).length > 0;
        },

        pageData() {
            return this.$store.getters["clients/pageData"];
        },

        clients() {
            return this.$store.getters["clients/clients"];
        },

        userProfile() {
            return this.$store.getters["auth/profile"];
        },

        refreshList() {
            return this.$store.getters["project/refreshList"];
        },

        clientListStats() {
            return this.$store.getters["clients/clientListStats"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        selectedUploadHeaders() {
            return this.clientUploadHeaders.filter(x => this.selectedUploadHeaderIds.includes(x.id));
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {                
                await this.handleFetchClients({ ...newVal }, true);
            },
            deep: true,
        },

        displayType(newType, oldType) {
            if (newType !== oldType) {
                this.sortClients();
            }
        },
    },

    async mounted() {
        this.selectedUploadHeaderIds = [ ...this.clientUploadHeaders.slice(0, 4).map(x => x.id) ];
        
        if (!this.clients.length && !this.clientListStats.totalClients) {
            await Promise.all([this.handleFetchStats, this.handleFetchClients]);
        }

        console.log("this.clientListStats.totalClients", this.clientListStats.totalClients);
        

        if (!this.clientListStats.totalClients) {
            await this.handleFetchStats();
        }
    },
    
    methods: {
        updateHeaderSelection(item, event) {
            this.selectedUploadHeaderIds = event;
        },

        openClientDetailsPage(row) {
            if (!row?._id) return;

            this.$router.push(`/dashboard/clients/${row._id}`);
        },

        closeFileModal() {
            this.showUploadModal = false;
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "clients");
        },

        async setImportType(val) {
            try {       
                if (["standard", "free"].includes(this.accountPlan)) {
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].clients?.create;
    
                    if (this.pageData?.total >= maxLimit) {
                        this.showUpgradeModalAlt = true;
                        return;
                    }
                }

                this.showUploadModal = true;
                this.importType = val;
            } catch (error) {
            }
        },

        async downloadClients(downloadType) {
            this.downloadLoading = true;
            this.$toast.info("Preparing file for download...");

            try {
                const headerType = downloadType === "csv" ? "text/csv" : "application/json";

                const resp = await this.$store.dispatch("clients/fetchClients", { 
                    params: { download: true, type: "all", downloadType }, 
                    refresh: true, 
                    config: {
                        responseType: "blob", // Important for handling binary data,
                        headers: {
                            "Accept": headerType,
                        },
                    },
                });
                
                // Set file name and MIME type
                const fileName = downloadType === "csv" ? "clients.csv" : "clients.json";
                const url = window.URL.createObjectURL(new Blob([resp], { type: headerType }));
                const link = document.createElement("a");
    
                link.href = url;
                link.setAttribute("download", fileName); // or the filename you want
                    
                document.body.appendChild(link);
                link.click();
    
                URL.revokeObjectURL(url);
                document.body.removeChild(link);
                
                this.$toast.success("File downloaded successfully");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.downloadLoading = false;
            }
        },

        closeCreateModal() {
            if (this.$route.query?.is_client) {
                this.$router.replace("/dashboard/clients");
            }
            this.showAddModal = false;
        },

        async createNewClient({ payload, resetFn }) {
            try {    
                this.createLoading = true;
                
                this.$store.dispatch("clients/addOptimisticUpdateList", payload);
                
                const resp = await this.$store.dispatch("clients/addClient", payload);
                
                this.$toast.success("Client created");
                resetFn();
                this.showAddModal = false;
                
                return this.$router.push({ name:"client-details-view", params:{ id: resp?._id } });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;

                if (errMsg.includes("upgrade your account")) {
                    this.showAddModal = false;
                    this.showUpgradeModalAlt = true;
                    return;
                }

                this.$toast.error(errMsg);
            } finally {
                this.createLoading = false;
            }
        },

        setDisplayType(val) {            
            if (this.displayType === val) {
                this.displayType = "";
                return;
            }
            this.displayType = val;
        },

        toggleDelete() {
            this.showDeleteModal = !this.showDeleteModal;
        },

        async deleteMultipleClients() {
            try {
                this.deleteLoading = true;
                
                const clientsToDelete = this.clients.filter(i => this.selectedClients.includes(i._id));
    
                if (clientsToDelete.length === 0) {
                    this.createLoading = false;
                    return;
                };

                await this.$store.dispatch("clients/deleteOptimisticUpdateList", this.selectedClients);
                this.$toast.success("Deleted");
                this.showDeleteModal = false;
                
                await this.$store.dispatch("clients/deleteClients", { ids: this.selectedClients, clientsToDelete });
                this.selectClients([]);
                this.deleteLoading = false;

                if (!this.clients.length && this.pageData.totalPages > 0) {
                    await this.handleFetchClients({ page: this.pageData.currentPage });
                }
                await this.handleFetchStats();
            } catch (error) {
                this.deleteLoading = false;
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            }
        },

        sortClients() {
            this.$store.dispatch("clients/sortClients", this.displayType);
        },

        selectClients(val) {            
            this.selectedClients = val;
        },

        async handleFetchClients(params, refresh = false) {
            try {
                this.fetchClients.loading = true;
                this.fetchClients.err = false;
                this.fetchClients.msg = "";

                const pageQueryObj = {
                    page: params?.page || 1,
                    ...params,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                await this.$store.dispatch("clients/fetchClients", { params: buildResult, refresh: refresh });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.fetchClients.err = true;
                this.fetchClients.msg = errMsg;
            } finally {
                this.fetchClients.loading = false;
            }
        },

        async handleFileUpload(files) {
            if (files) {
                if (this.importType === "csv") {
                    await this.parseCSVFile(files);
                } else { // JSON uploads
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        const jsonData = event.target.result;
                        this.parseJSONFile(jsonData);
                    };

                    reader.readAsText(files);
                }
            }
        },

        async parseJSONFile(jsonData) {
            try {                
                this.uploadErr = false;

                if (!jsonData) {
                    this.$toast.error("Invalid JSON file");
                    return;
                }                
                
                await this.$store.dispatch("clients/uploadMutlipleClients", { 
                    payload: {
                        clients: jsonData,
                    }, 
                    config: {
                        onUploadProgress: this.onUploadProgress, 
                        header: { "Content-Type": "application/json" } ,
                        method: "POST",
                    },
                });
                
                this.$toast.success("Files uploaded");
                this.importType = "";
                this.showUploadModal = false;

                await this.handleFetchStats();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message || "Upload failed";
                
                this.$toast.error(errMsg);
                this.uploadErr = true;
            } finally {
                this.uploadProgress = 0;
            }
        },

        parseCSVFile(file) {
            Papa.parse(file, {
                header: true,
                complete: this.processCSVData,
                error: this.handleParseError,
            });
        },

        onUploadProgress(event) {
            this.uploadProgress = Math.round((event.loaded * 100) / event.total);
        },

        async processCSVData(results) {
            try {
                this.uploadErr = false;

                if (results?.error?.length) {
                    this.$toast.error(`Invalid content in ${this.importType} file`);
                    return;
                }

                const clients = results.data;                
                this.uploadContent = clients;

                const formData = new FormData();
                formData.append("clients", JSON.stringify(clients));
                
                await this.$store.dispatch("clients/uploadMutlipleClients", { 
                    payload: formData, 
                    config: {
                        onUploadProgress: this.onUploadProgress,
                        header: { "Content-Type": "multipart/form-data" },
                    },
                });
                
                this.$toast.success("File uploaded successfully");

                this.importType = "";
                this.uploadProgress = 0;
                this.showUploadModal = false;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                
                this.$toast.error(errMsg);
                this.uploadProgress = 0;
                this.uploadErr = true;
            }
        },

        handleParseError(error) {
            console.error("Error parsing CSV file", error);
        },

        async handleFetchStats() {
            try {
                this.fetchStatsErr = false;
                this.statsLoading = true;

                await this.$store.dispatch("clients/fetchStats", {});
            } catch (error) {
                this.fetchStatsErr = true;
            } finally {
                this.statsLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .revenue__stats__list {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 20px 10px;
    }
</style>